import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, Renderer2, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { first } from 'rxjs/internal/operators/first';
import { Subscription } from 'rxjs';

import { BaseComponent } from 'src/app/components/base/base.component';
import { EnvironmentService } from 'src/app/services/environment.service';
import { LanguageKey } from '../../enums/language.enum';
import { LanguageService } from '../../services/language.service';
import { LanguageSwitchModalComponent } from '../language-switch-modal/language-switch-modal.component';


@Component({
    templateUrl: './language-switch-button.component.html',
    styleUrls: [
        './../../../../../styles.scss',
        './language-switch-button.component.scss'
    ],
    encapsulation: ViewEncapsulation.ShadowDom
})

export class LanguageSwitchButtonComponent extends BaseComponent implements OnInit, OnChanges, OnDestroy {
    /** e.g. selected-language="ES" */
    @Input() selectedLanguage!: LanguageKey;
    /** reload-page-on-language-change="true" */
    @Input() reloadPageOnLanguageChange = false;
    /** update-all-links-on-language-change="true" */
    @Input() updateAllLinksOnLanguageChange = false;
    /** update-all-links-on-init="true" */
    @Input() updateAllLinksOnInit = false;
    @Output() selectedLanguageChanged = new EventEmitter<LanguageKey>();

    private langSubscription!: Subscription;

    selectedLanguageLabel!: string;

    constructor(
        private modalService: BsModalService,
        private languageService: LanguageService,
        protected override environmentService: EnvironmentService,
        private renderer2: Renderer2,
    ) {
        super(environmentService);
     }

    ngOnInit() {
        this.renderOneLinkScript();
        this.init();

        if (this.updateAllLinksOnInit) {
            this.languageService.updateAllLinksOnPage(this.selectedLanguage);
        }
    }

    ngOnChanges(changes: SimpleChanges) {
		if (changes['customEnvironment']) {
            this.defineEnvironment();
        }
	}

    ngOnDestroy(): void {
        this.langSubscription?.unsubscribe();
    }

    private init() {
        const lang = this.languageService.getCurrentLanguageFromUrl()
        this.languageService.setCurrentLanguage(this.selectedLanguage || lang);
        this.selectedLanguage = this.languageService.getCurrentLanguage();
        this.selectedLanguageLabel = this.languageService.getLanguageLabel(this.selectedLanguage);
    }

    private renderOneLinkScript() {
        this.langSubscription = this.languageService.currentLanguageSubscription.subscribe(langKey => {
            if (!this.languageService.isDefaultLanguage(langKey)) {
                this.languageService.loadOneLinkScript(this.renderer2);
            }
        });
    }

    selectLanguage(langCode: LanguageKey | string) {
        this.selectedLanguage = langCode as LanguageKey;
        this.selectedLanguageLabel = this.languageService.getLanguageLabel(this.selectedLanguage);
        this.languageService.setCurrentLanguage(this.selectedLanguage);
        this.selectedLanguageChanged.emit(this.selectedLanguage);

        this.languageService.updateCurrentUrlWithLanguageParam(this.selectedLanguage as LanguageKey);

        if (this.reloadPageOnLanguageChange) {
            location.reload();
        }

        if (this.updateAllLinksOnLanguageChange) {
            this.languageService.updateAllLinksOnPage(this.selectedLanguage);
        }
    }

    isCurrentLanguage(langCode: string): boolean {
        return this.languageService.isCurrentLanguage(langCode as LanguageKey);
    }

    openLanguageSwitchModal() {
        const modalRef = this.modalService.show(LanguageSwitchModalComponent, {
            initialState: {
                selectedLanguage: this.selectedLanguage
            },
            class: 'modal-drawer modal-drawer__left',
        });

        modalRef?.content?.selectedLanguageChanged.pipe(first()).subscribe(lang => {
            this.selectLanguage(lang);
        })
    }
}
